import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';

/** Refusals the debit-authorization gauntlet raises for subscribe and switch alike. */
const DEBIT_AUTHORIZATION_MESSAGES: Partial<Record<ApiErrorCode, string>> = {
  [ApiErrorCode.SUBSCRIBE_ACCOUNT_NEEDS_RECONNECT]:
    'This bank connection needs to be reconnected before it can be charged. Reconnect it and try again.',
  [ApiErrorCode.SUBSCRIBE_ACCOUNT_NOT_DEBITABLE]:
    'That account cannot be debited. Choose a checking or savings account.',
  [ApiErrorCode.SUBSCRIBE_NAME_REQUIRED]:
    'Add your first and last name in settings before subscribing — your bank requires it to authorize a debit.',
  [ApiErrorCode.FINANCIAL_ACCOUNT_NOT_FOUND]: 'We could not find that account. Choose another and try again.',
};

const CHECKOUT_ERROR_MESSAGES: Partial<Record<ApiErrorCode, string>> = {
  ...DEBIT_AUTHORIZATION_MESSAGES,
  [ApiErrorCode.SUBSCRIBE_CARD_SETUP_INCOMPLETE]:
    'Your card was not confirmed. Go back and enter your card details again.',
  [ApiErrorCode.SUBSCRIBE_CARD_SETUP_NOT_FOUND]:
    'We could not find that card setup. Go back and enter your card details again.',
  [ApiErrorCode.SUBSCRIBE_IN_PROGRESS]:
    'A subscription is already being set up for this household. Give it a moment, then refresh.',
  [ApiErrorCode.SUBSCRIBE_QUOTE_EXPIRED]:
    'This page sat open long enough that the first charge date changed. Review the updated date and try again.',
};

const FALLBACK_MESSAGE = 'Something went wrong starting your subscription. Please try again.';

/**
 * The message for a refusal, or null when the caller should not show one.
 *
 * Returns null for the acknowledgment refusal (the flow handles it) and for
 * `SUBSCRIBE_ALREADY_SUBSCRIBED`, which after a submit means the subscription
 * exists — the household got what it asked for, and the caller advances rather
 * than reporting a failure.
 */
export function checkoutErrorMessage(code: ApiErrorCode): string | null {
  if (code === ApiErrorCode.SUBSCRIBE_THIRD_PARTY_ACK_REQUIRED || code === ApiErrorCode.SUBSCRIBE_ALREADY_SUBSCRIBED) {
    return null;
  }
  return CHECKOUT_ERROR_MESSAGES[code] ?? FALLBACK_MESSAGE;
}

const SWITCH_ERROR_MESSAGES: Partial<Record<ApiErrorCode, string>> = {
  ...DEBIT_AUTHORIZATION_MESSAGES,
  // Overrides the shared copy's "before subscribing": the member switching
  // accounts is already subscribed.
  [ApiErrorCode.SUBSCRIBE_NAME_REQUIRED]:
    'Add your first and last name in settings before switching accounts — your bank requires it to authorize a debit.',
  [ApiErrorCode.SWITCH_PAYMENT_SAME_ACCOUNT]: 'Your membership already debits this account. Choose a different one.',
  [ApiErrorCode.SWITCH_PAYMENT_IN_PROGRESS]:
    'An account switch is already in progress. Give it a moment, then refresh.',
  [ApiErrorCode.SWITCH_PAYMENT_NO_SUBSCRIPTION]:
    'There is no active membership to switch. Refresh the page and try again.',
  [ApiErrorCode.SWITCH_PAYMENT_TERMS_CHANGED]:
    'Your membership terms changed while this page was open. Review the updated amount and try again.',
};

const SWITCH_FALLBACK_MESSAGE = 'Something went wrong switching your payment account. Please try again.';

/**
 * The message for a refused switch, or null when the caller should not show
 * one — the acknowledgment refusal is control flow, answered by revealing the
 * disclosure and resubmitting.
 */
export function switchAccountErrorMessage(code: ApiErrorCode): string | null {
  if (code === ApiErrorCode.SUBSCRIBE_THIRD_PARTY_ACK_REQUIRED) {
    return null;
  }
  return SWITCH_ERROR_MESSAGES[code] ?? SWITCH_FALLBACK_MESSAGE;
}
